import { useState } from 'react';

import {
  useGetCategories,
  useGetExercises,
  useGetObjectiveAttributes,
  useGetTherapyAttributes,
  useGetThemes,
} from '@/api/glow-up/glow-up.hooks';
import { ErrorScreen } from '@/components/ui/error-screen';
import { LoadingScreen } from '@/components/ui/loading-screen';
import { Pagination } from '@/components/ui/pagination';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AddCategory } from './category/AddCategory';
import { CategoryList } from './category/CategoryList';
import { AddExercise } from './exercise/AddExercise';
import { ExerciseList } from './exercise/ExerciseList';
import { AddObjectiveAttribute } from './objective-attribute/AddObjectiveAttribute';
import { ObjectiveAttributeList } from './objective-attribute/ObjectiveAttributeList';
import { AddTheme } from './theme/AddTheme';
import { ThemeList } from './theme/ThemeList';
import { AddTherapyAttribute } from './therapy-attribute/AddTherapyAttribute';
import { TherapyAttributeList } from './therapy-attribute/TherapyAttributeList';

export const GlowUpManagement = () => {
  const ITEMS_PER_PAGE = 10;

  const [activeTab, setActiveTab] = useState('themes');
  const [themesPage, setThemesPage] = useState(1);
  const [categoriesPage, setCategoriesPage] = useState(1);
  const [exercisesPage, setExercisesPage] = useState(1);

  const handleTabChange = (value: string) => {
    // Only reset pagination if the tab actually changed
    if (value !== activeTab) {
      if (value === 'themes') setThemesPage(1);
      if (value === 'categories') setCategoriesPage(1);
      if (value === 'exercises') setExercisesPage(1);
      setActiveTab(value);
    }
  };

  const {
    themes,
    count: themesCount,
    isLoading: themesLoading,
    isError: themesError,
    refetch: refetchThemes,
  } = useGetThemes({
    skip: (themesPage - 1) * ITEMS_PER_PAGE,
    take: ITEMS_PER_PAGE,
  });

  const {
    categories,
    count: categoriesCount,
    isLoading: categoriesLoading,
    isError: categoriesError,
    refetch: refetchCategories,
  } = useGetCategories({
    skip: (categoriesPage - 1) * ITEMS_PER_PAGE,
    take: ITEMS_PER_PAGE,
  });
  const {
    exercises,
    count: exercisesCount,
    isLoading: exercisesLoading,
    isError: exercisesError,
    refetch: refetchExercises,
  } = useGetExercises({
    skip: (exercisesPage - 1) * ITEMS_PER_PAGE,
    take: ITEMS_PER_PAGE,
  });
  const {
    objectiveAttributes,
    isLoading: objectiveAttributesLoading,
    isError: objectiveAttributesError,
    refetch: refetchObjectiveAttributes,
  } = useGetObjectiveAttributes();
  const {
    therapyAttributes,
    isLoading: therapyAttributesLoading,
    isError: therapyAttributesError,
    refetch: refetchTherapyAttributes,
  } = useGetTherapyAttributes();

  if (
    themesLoading ||
    categoriesLoading ||
    exercisesLoading ||
    objectiveAttributesLoading ||
    therapyAttributesLoading
  ) {
    return <LoadingScreen />;
  }

  if (
    themesError ||
    categoriesError ||
    exercisesError ||
    objectiveAttributesError ||
    therapyAttributesError
  ) {
    return <ErrorScreen />;
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Gestion Glow Up</h1>
      </div>

      <Tabs
        value={activeTab}
        className="w-full"
        onValueChange={handleTabChange}
      >
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="themes">Thèmes</TabsTrigger>
          <TabsTrigger value="categories">Catégories</TabsTrigger>
          <TabsTrigger value="exercises">Exercices</TabsTrigger>
          <TabsTrigger value="objectiveAttributes">
            Attributs objectifs
          </TabsTrigger>
          <TabsTrigger value="therapyAttributes">
            Attributs thérapie
          </TabsTrigger>
        </TabsList>

        <TabsContent value="themes" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">Thèmes</h2>
            <AddTheme refetch={refetchThemes} />
          </div>
          {themes && <ThemeList themes={themes} refetch={refetchThemes} />}
          {themes && themes.length > 0 && (
            <Pagination
              currentPage={themesPage}
              totalItems={themesCount}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setThemesPage}
              className="mt-4"
            />
          )}
        </TabsContent>

        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">Catégories</h2>
            <AddCategory refetch={refetchCategories} />
          </div>
          {categories && (
            <CategoryList categories={categories} refetch={refetchCategories} />
          )}
          {categories && categories.length > 0 && (
            <Pagination
              currentPage={categoriesPage}
              totalItems={categoriesCount}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setCategoriesPage}
              className="mt-4"
            />
          )}
        </TabsContent>

        <TabsContent value="exercises" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">Exercices</h2>
            <AddExercise refetch={refetchExercises} />
          </div>
          {exercises && (
            <ExerciseList exercises={exercises} refetch={refetchExercises} />
          )}
          {exercises && exercises.length > 0 && (
            <Pagination
              currentPage={exercisesPage}
              totalItems={exercisesCount}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={setExercisesPage}
              className="mt-4"
            />
          )}
        </TabsContent>

        <TabsContent value="objectiveAttributes" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">Attributs d&apos;objectif</h2>
            <AddObjectiveAttribute refetch={refetchObjectiveAttributes} />
          </div>
          <ObjectiveAttributeList
            attributes={objectiveAttributes}
            refetch={refetchObjectiveAttributes}
          />
        </TabsContent>

        <TabsContent value="therapyAttributes" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold">
              Attributs de thérapie
            </h2>
            <AddTherapyAttribute refetch={refetchTherapyAttributes} />
          </div>
          <TherapyAttributeList
            attributes={therapyAttributes}
            refetch={refetchTherapyAttributes}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
